import { z } from 'zod';
/**
 * Настройки биллинговой части панели. Сегодня в них ровно одно число — порог,
 * начиная с которого клиент считается КРУПНЫМ ПЛАТЕЛЬЩИКОМ («китом»).
 *
 * Отдельный файл, а не поле в `llm-settings.ts`: тот контракт — набор булевых
 * тумблеров с общей формой `{enabled}` и общим экраном «LLM», а здесь число со
 * своей валидацией, своим правом и своим экраном в разделе «Чарджбеки». Слить
 * их значило бы протащить `whaleMinCharges` через `UpdateLlmSettingsRequest`,
 * где каждое поле — `boolean.optional()`.
 *
 * КЛЮЧ настройки (`billing.whale_min_charges`) и значение по умолчанию (6)
 * живут в `@wit-support/db` (`packages/db/src/whale.ts`) и СЮДА НЕ КОПИРУЮТСЯ:
 * их читают все поверхности, где рисуется бейдж кита, и второй экземпляр
 * константы — это ровно тот рассинхрон, ради которого порог и вынесли в одно
 * место. Роут админки импортирует их оттуда напрямую; клиенту же ни ключ, ни
 * дефолт не нужны — GET отдаёт готовое значение и флаг {@link BillingSettings.isDefault}.
 */
/**
 * Верхняя граница порога. Существует не потому, что 10 001 списание
 * невозможно, а потому, что опечатка в десять знаков («60» набрано как
 * «6000000000») не должна проходить проверку: она молча выключает бейдж кита
 * для всей базы, и заметить это можно только по отсутствию метки там, где она
 * была вчера. Настоящее число списаний у живого клиента — десятки, у самых
 * долгих — сотни; 10 000 оставляет запас на два порядка и всё ещё ловит
 * промах по клавиатуре.
 */
export const WHALE_MIN_CHARGES_MAX = 10_000;
/**
 * WRITE-граница: форма значения, которую записываем в `app_settings` под
 * ключом `billing.whale_min_charges`. `.strict()` — на записи лишнее или
 * опечатанное поле должно быть отвергнуто, а не сохранено молча.
 *
 * Это КАНОНИЧЕСКАЯ форма, и записывать следует только её.
 */
export const WhaleMinChargesSetting = z
    .object({ minCharges: z.number().int().min(1).max(WHALE_MIN_CHARGES_MAX) })
    .strict();
/**
 * READ-граница: что мы готовы ПРОЧИТАТЬ из `app_settings`. Union, а не одна
 * форма, и это не небрежность — читатель обязан вести себя ровно как
 * `readWhaleMinCharges` из `@wit-support/db`, который терпит и голое число.
 * Разъехавшись, они дали бы худший из возможных исходов: панель показывает
 * «действует значение по умолчанию, 6», а бейдж в инбоксе считает по 8.
 *
 * Объектная ветка `.passthrough()` — терпимость к раскатке: писатель поновее,
 * добавивший в jsonb соседнее поле, не должен выглядеть для старого читателя
 * как «настройка не задана».
 *
 * Верхняя граница здесь НЕ проверяется намеренно: она защищает от опечатки на
 * ВВОДЕ, а уже сохранённое значение (пусть и выставленное до появления
 * границы) — это осознанное решение оператора, и подменять его дефолтом
 * значило бы тихо изменить поведение бейджа. Всё, что не целое ≥ 1, — не
 * порог, и ответ на него один: значение по умолчанию.
 */
export const WhaleMinChargesStoredSetting = z.union([
    z
        .object({ minCharges: z.number().int().min(1) })
        .passthrough()
        .transform((v) => v.minCharges),
    z.number().int().min(1),
]);
/** READ-граница ответа `GET /api/billing-settings`. */
export const BillingSettings = z.object({
    /**
     * ДЕЙСТВУЮЩИЙ порог: явно заданный оператором либо значение по умолчанию.
     * Поверхности, которые рисуют бейдж, используют ровно это число.
     */
    whaleMinCharges: z.number().int().min(1),
    /**
     * `true` — порог НЕ задан явно (строки в `app_settings` нет либо в ней
     * мусор), работает значение по умолчанию.
     *
     * Поле обязательное, а не «удобное»: без него оператор не отличает
     * «поставили 6» от «никто не настраивал», а это разные ситуации — во второй
     * число может измениться под ним при обновлении дефолта.
     */
    isDefault: z.boolean(),
});
/**
 * WRITE-граница `PATCH /api/billing-settings`.
 *
 * Поле ОБЯЗАТЕЛЬНОЕ (в отличие от частичного патча настроек LLM): в этом
 * контракте оно единственное, и пустое тело значило бы «ничего не менять» —
 * запрос, который незачем отправлять. `.strict()` отвергает опечатки в имени
 * поля, иначе `{whaleMinCharge: 8}` вернул бы 200 и не изменил ничего.
 */
export const UpdateBillingSettingsRequest = z
    .object({
    whaleMinCharges: z
        .number()
        .int('Порог — целое число списаний')
        .min(1, 'Порог не может быть меньше 1')
        .max(WHALE_MIN_CHARGES_MAX, `Порог не может быть больше ${WHALE_MIN_CHARGES_MAX}`),
})
    .strict();
//# sourceMappingURL=billing-settings.js.map